import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { Badge, Modal, ErrorNote } from '../components/ui.jsx';

const SITE_STATUS_BADGE = { ACTIVE: 'active', COMPLETED: 'administrative', ON_HOLD: 'half-day' };

function SitesTab({ canManage }) {
  const [sites, setSites] = useState([]);
  const [modal, setModal] = useState(null); // null | 'add' | site
  const [form, setForm] = useState({ name: '', location: '', status: 'ACTIVE' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api('/sites').then(setSites).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (modal === 'add') await api('/sites', { method: 'POST', body: form });
      else await api(`/sites/${modal.id}`, { method: 'PATCH', body: form });
      setModal(null); load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="filter-bar">
        <div style={{ flex: 1 }} />
        {canManage && (
          <button className="btn btn-primary" onClick={() => { setForm({ name: '', location: '', status: 'ACTIVE' }); setModal('add'); setError(''); }}>
            + Add Site
          </button>
        )}
      </div>
      {!modal && <ErrorNote error={error} />}
      <div className="card emp-table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Location</th><th>Status</th><th>Active Employees</th>{canManage && <th></th>}</tr></thead>
          <tbody>
            {sites.map((s) => (
              <tr key={s.id}>
                <td className="fw-bold">{s.name}</td>
                <td className="text-muted">{s.location || '—'}</td>
                <td><Badge kind={SITE_STATUS_BADGE[s.status]}>{s.status.replace('_', ' ')}</Badge></td>
                <td>{s._count.employees}</td>
                {canManage && (
                  <td><button className="btn btn-ghost btn-sm" onClick={() => {
                    setForm({ name: s.name, location: s.location ?? '', status: s.status }); setModal(s); setError('');
                  }}>Edit</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Add Site' : `Edit ${modal?.name ?? ''}`}>
        <form onSubmit={save}>
          <div className="form-group"><label>Site Name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required /></div>
          <div className="form-group"><label>Location</label>
            <input className="form-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></div>
          {modal !== 'add' && (
            <div className="form-group"><label>Status</label>
              <select className="form-select" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="ACTIVE">Active</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="COMPLETED">Completed</option>
              </select></div>
          )}
          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// Departments and Employment Types are simple named lists — same shape,
// different endpoint — so one component covers both.
function NamedListTab({ label, endpoint, canManage }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // null | 'add' | item
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api(endpoint).then(setItems).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [endpoint]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (modal === 'add') await api(endpoint, { method: 'POST', body: { name } });
      else await api(`${endpoint}/${modal.id}`, { method: 'PATCH', body: { name } });
      setModal(null); load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="filter-bar">
        <div style={{ flex: 1 }} />
        {canManage && (
          <button className="btn btn-primary" onClick={() => { setName(''); setModal('add'); setError(''); }}>
            + Add {label}
          </button>
        )}
      </div>
      {!modal && <ErrorNote error={error} />}
      <div className="card emp-table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Active Employees</th>{canManage && <th></th>}</tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="fw-bold">{it.name}</td>
                <td>{it._count.employees}</td>
                {canManage && (
                  <td><button className="btn btn-ghost btn-sm" onClick={() => { setName(it.name); setModal(it); setError(''); }}>Rename</button></td>
                )}
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3}><div className="empty-state"><p>No {label.toLowerCase()}s yet.</p></div></td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? `Add ${label}` : `Rename ${modal?.name ?? ''}`}>
        <form onSubmit={save}>
          <div className="form-group"><label>{label} Name</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></div>
          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default function Organization() {
  const { can } = useAuth();
  const tabs = [
    { key: 'sites', label: 'Sites', show: true, canManage: can('sites.manage') },
    { key: 'departments', label: 'Departments', show: true, canManage: can('departments.manage') },
    { key: 'types', label: 'Employment Types', show: true, canManage: can('employment_types.manage') },
  ];
  const [tab, setTab] = useState('sites');

  return (
    <>
      <div className="filter-bar">
        <div className="segmented">
          {tabs.map((t) => (
            <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === 'sites' && <SitesTab canManage={can('sites.manage')} />}
      {tab === 'departments' && <NamedListTab label="Department" endpoint="/departments" canManage={can('departments.manage')} />}
      {tab === 'types' && <NamedListTab label="Employment Type" endpoint="/employment-types" canManage={can('employment_types.manage')} />}
    </>
  );
}
